import { createHttpClient } from '@adapters/HttpClient';
import { CourierInvoicesApiAdapter } from '../../adapters';
import type { CreateCourierInvoiceBody, CreateCourierInvoiceResponse } from '../../models/types';
import { apiClientWH } from '@shared/services/ApiClient';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useToast } from '@shared/hooks/useToast';
import { useTranslation } from 'react-i18next';

export const useCreateCourierInvoice = () => {
  const { toast } = useToast();
  const { t } = useTranslation('courierInvoicesModule');
  const queryClient = useQueryClient();
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const adapter = useMemo(() => new CourierInvoicesApiAdapter(httpClient), [httpClient]);

  const mutation = useMutation({
    mutationFn: (body: CreateCourierInvoiceBody): Promise<CreateCourierInvoiceResponse> => adapter.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['courier-invoices-infinite'] });
      toast({
        title: t('create.success_title'),
        description: t('create.success_description'),
      });
    },
    onError: (error: { response?: { data?: { message?: string } }; message?: string }) => {
      const msg = error?.response?.data?.message ?? error?.message ?? t('errors.unexpected');
      toast({
        title: t('create.error_title'),
        description: msg,
        variant: 'destructive',
      });
    },
  });

  return {
    createInvoiceAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
  };
};
